import React, { useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import payrollService from '../../../services/payrollService';
import { useEmployeeSelfService } from '../../../contexts/EmployeeSelfServiceContext';
import { formatDateFromApi } from '../../../utils/attendanceDateTime';

const formatPayPeriodMonth = (record) => {
  const dateStr = record.pay_period_start || record.pay_period_end;
  if (dateStr) {
    const [y, m] = String(dateStr).split('T')[0].split('-').map(Number);
    if (y && m) {
      return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    }
  }
  if (record.pay_period_month && record.pay_period_year) {
    return new Date(record.pay_period_year, record.pay_period_month - 1, 1).toLocaleDateString('en-IN', {
      month: 'long',
      year: 'numeric',
    });
  }
  return '—';
};

const formatPayPeriodRange = (record) => {
  const start = formatDateFromApi(record.pay_period_start);
  const end = formatDateFromApi(record.pay_period_end);
  if (start === '-' && end === '-') return '—';
  if (start === end) return start;
  return `${start} – ${end}`;
};

const getPeriodSortKey = (record) => record.pay_period_start || record.pay_period_end || '';

const PayslipSection = ({ onNotify }) => {
  const { payslips } = useEmployeeSelfService();
  const [loadingId, setLoadingId] = useState(null);

  const sortedPayslips = useMemo(
    () => [...payslips].sort((a, b) => getPeriodSortKey(b).localeCompare(getPeriodSortKey(a))),
    [payslips]
  );

  const handleDownload = async (record) => {
    setLoadingId(record.id);
    try {
      const result = await payrollService.downloadPayslip(record.id);
      if (!result.success) {
        onNotify?.({ message: result.error || 'Download failed', type: 'error' });
      }
    } finally {
      setLoadingId(null);
    }
  };

  const handleView = async (record) => {
    setLoadingId(record.id);
    try {
      const result = await payrollService.viewPayslip(record.id);
      if (!result.success) {
        onNotify?.({ message: result.error || 'Preview failed', type: 'error' });
      }
    } finally {
      setLoadingId(null);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
      amount || 0
    );

  const getDeductions = (record) => {
    const gross = parseFloat(record.gross_salary || 0);
    const net = parseFloat(record.net_salary || 0);
    if (gross > 0 && net >= 0) return Math.max(0, gross - net);
    return 0;
  };

  const statusColor = (status) => {
    if (status === 'approved' || status === 'locked') return 'bg-green-100 text-green-800';
    if (status === 'reviewed') return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div>
        <h2 className="text-xl font-semibold">Payslips</h2>
        <p className="text-sm text-muted-foreground">View and download your salary slips month-wise</p>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-2 text-left">Month</th>
              <th className="px-4 py-2 text-left">Pay Period</th>
              <th className="px-4 py-2 text-right">Gross</th>
              <th className="px-4 py-2 text-right">Deductions</th>
              <th className="px-4 py-2 text-right">Net</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedPayslips.map((record) => (
              <tr key={record.id} className="border-t">
                <td className="px-4 py-3 font-medium whitespace-nowrap">{formatPayPeriodMonth(record)}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatPayPeriodRange(record)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(record.gross_salary)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">{formatCurrency(getDeductions(record))}</td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatCurrency(record.net_salary)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs capitalize ${statusColor(record.status)}`}>
                    {record.status || 'draft'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Eye"
                      disabled={loadingId === record.id}
                      onClick={() => handleView(record)}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      iconName="Download"
                      disabled={loadingId === record.id}
                      onClick={() => handleDownload(record)}
                    >
                      Download
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedPayslips.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="FileText" size={40} className="mx-auto mb-2 opacity-50" />
            <p>No payslips available yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayslipSection;
